import React, { useState } from 'react';
import {
  Clock,
  AlertCircle,
  MessageSquare,
  CheckCircle,
  XCircle,
  RefreshCw,
  Info
} from 'lucide-react';
import moment from 'moment';
import { toast } from 'sonner';
import { useListConversationWindowsQuery, ConversationWindowItem } from '@/client/api/whatsapp/list-conversation-windows.query';
import { useExpireConversationWindowMutation } from '@/client/api/whatsapp/expire-conversation-window.mutation';

type WindowStatus = 'open' | 'expiring' | 'closed';

const statusFor = (item: ConversationWindowItem): WindowStatus => {
  if (item.expires_in_seconds <= 0) return 'closed';
  if (item.expires_in_seconds <= 3600) return 'expiring';
  return 'open';
};

const windowStatusConfig: Record<WindowStatus, { label: string; style: string; icon: React.ReactNode }> = {
  open: { label: 'Open', style: 'bg-green-100 text-green-700 border-green-200', icon: <CheckCircle className="w-3 h-3" /> },
  expiring: { label: 'Expiring Soon', style: 'bg-yellow-100 text-yellow-700 border-yellow-200', icon: <Clock className="w-3 h-3" /> },
  closed: { label: 'Closed', style: 'bg-gray-100 text-gray-600 border-gray-200', icon: <XCircle className="w-3 h-3" /> }
};

const ConversationWindowPage: React.FC = () => {
  const { data, isLoading, refetch } = useListConversationWindowsQuery();
  const windows = data?.data?.windows || [];
  const expireMutation = useExpireConversationWindowMutation();
  const [filter, setFilter] = useState<'all' | WindowStatus>('all');

  const filtered = filter === 'all' ? windows : windows.filter(w => statusFor(w) === filter);

  const handleExpire = (item: ConversationWindowItem) => {
    expireMutation.mutate(
      { phone: item.phone },
      {
        onSuccess: () => refetch(),
        onError: (error) => toast.error(error.message || 'Failed to expire the window'),
      }
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Conversation Window</h1>
            <p className="text-sm text-gray-500 mt-1">Monitor 24-hour WhatsApp conversation windows</p>
          </div>
          <button
            onClick={() => refetch()}
            className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
        </div>
      </div>

      <div className="p-6">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
            <div>
              <h3 className="text-sm font-semibold text-blue-900 mb-1">About the 24-Hour Conversation Window</h3>
              <p className="text-sm text-blue-800">
                WhatsApp Business allows sending free-form messages within 24 hours of the last inbound message from a customer.
                After the window closes, you can only send pre-approved template messages.
              </p>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-600">Total Windows</span>
              <MessageSquare className="w-5 h-5 text-blue-500" />
            </div>
            <p className="text-2xl font-bold text-gray-900">{windows.length}</p>
          </div>
          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-600">Open</span>
              <CheckCircle className="w-5 h-5 text-green-500" />
            </div>
            <p className="text-2xl font-bold text-gray-900">{windows.filter(w => statusFor(w) === 'open').length}</p>
          </div>
          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-600">Expiring Soon</span>
              <AlertCircle className="w-5 h-5 text-yellow-500" />
            </div>
            <p className="text-2xl font-bold text-gray-900">{windows.filter(w => statusFor(w) === 'expiring').length}</p>
          </div>
          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-600">Closed</span>
              <XCircle className="w-5 h-5 text-gray-400" />
            </div>
            <p className="text-2xl font-bold text-gray-900">{windows.filter(w => statusFor(w) === 'closed').length}</p>
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="flex gap-2 mb-4">
          {(['all', 'open', 'expiring', 'closed'] as const).map(f => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-4 py-2 text-sm rounded-lg capitalize transition-colors ${
                filter === f ? 'bg-green-500 text-white' : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
            >
              {f === 'all' ? 'All' : f === 'expiring' ? 'Expiring Soon' : f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>

        {/* Table */}
        <div className="bg-white rounded-lg border border-gray-200">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Phone</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Inbound</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Expires In</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {isLoading && (
                  <tr><td colSpan={5} className="px-6 py-4 text-sm text-gray-500 text-center">Loading...</td></tr>
                )}
                {filtered.map(win => {
                  const status = statusFor(win);
                  const cfg = windowStatusConfig[status];
                  return (
                    <tr key={win.phone} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-green-500 to-blue-600 flex items-center justify-center text-white font-semibold text-sm">
                            {win.phone.slice(-2)}
                          </div>
                          <p className="text-sm font-medium text-gray-900">{win.phone}</p>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {moment.unix(win.last_inbound_at).fromNow()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {status === 'closed' ? '—' : moment.duration(win.expires_in_seconds, 'seconds').humanize()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium border ${cfg.style}`}>
                          {cfg.icon}{cfg.label}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        {status !== 'closed' && (
                          <button
                            onClick={() => handleExpire(win)}
                            className="px-3 py-1 text-xs bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                          >
                            Expire Now
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {!isLoading && filtered.length === 0 && (
            <div className="text-center py-12">
              <Clock className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No conversation windows found</h3>
              <p className="text-sm text-gray-500">Try a different filter</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ConversationWindowPage;
